import NetInfo from '@react-native-community/netinfo';
import { Platform } from 'react-native';
import { getOfflineDatabase, getOfflineReviewService, getCacheService } from './platformServices';
import type {
  AdminReviewItem,
  Photo,
  ReviewItem,
  ReviewLifecycleResponse,
  ReviewTemplate,
  Room,
  SharedCategoryLink,
  SharedItemLink,
  SharedQuestionMapping,
  UploadedPhoto,
} from '@exhale/contracts';
import type { HomeReview, ReviewContactDetails, ReviewResponse } from '../types';
import {
  applyDetoxNetworkOverride,
  getEffectiveNetworkOnline,
  subscribeToDetoxNetworkOverride,
} from '../utils/detoxTestControls';
import {
  reviewAdminApi,
  AdminReviewItemInput,
  AdminReviewItemUpdate,
  ItemLinkOptions,
  QuestionMappingInput,
} from './reviewAdminApi';
import {
  reviewVendorEmailApi,
  SendVendorEmailResult,
  VendorEmailStatus,
} from './reviewVendorEmailApi';
import {
  reviewPhotoApi,
  PhotoUploadInput,
  PresignedPhotoUrl,
} from './reviewPhotoApi';
import {
  reviewCoreApi,
  OnDemandItemInput,
  OnDemandItemUpdate,
  ReviewDetailsOptions,
  ReviewReportDelivery,
  ReviewReportResendResult,
} from './reviewCoreApi';
import { ReviewSnapshotCacheService } from './reviewSnapshotCacheService';
import { ReviewTemplateCacheService } from './reviewTemplateCacheService';
import { ReviewRoomService } from './reviewRoomService';
import { ReviewOnDemandItemService } from './reviewOnDemandItemService';
import { ReviewMetadataService } from './reviewMetadataService';
import { ReviewResponseService } from './reviewResponseService';
import { ReviewPhotoService } from './reviewPhotoService';
import { ReviewCreateOptions, ReviewCreationService } from './reviewCreationService';
import { ReviewReadService } from './reviewReadService';
import { ReviewLifecycleService } from './reviewLifecycleService';
import { shouldUseOfflineReviewWorkflow } from '../config/offlineWorkflow';

// Get platform-specific services
const offlineDatabase = getOfflineDatabase();
const offlineReviewService = getOfflineReviewService();
const cacheService = getCacheService();

class ReviewService {
  private isOnline: boolean = true;
  private offlineReviewService: any = null;
  private templateCacheService: ReviewTemplateCacheService;
  private snapshotCacheService: ReviewSnapshotCacheService;
  private roomService: ReviewRoomService;
  private onDemandItemService: ReviewOnDemandItemService;
  private metadataService: ReviewMetadataService;
  private responseService: ReviewResponseService;
  private photoService: ReviewPhotoService;
  private creationService: ReviewCreationService;
  private readService: ReviewReadService;
  private lifecycleService: ReviewLifecycleService;

  constructor() {
    // Only initialize network listener on mobile
    if (Platform.OS !== 'web') {
      this.initializeNetworkListener();
    }
    // Set offline review service
    this.offlineReviewService = offlineReviewService;
    this.creationService = new ReviewCreationService({
      offlineReviewService: this.offlineReviewService,
      onlineApi: reviewCoreApi,
      getIsOnline: () => this.isOnline,
      useOfflineWorkflow: shouldUseOfflineReviewWorkflow,
      isWeb: () => Platform.OS === 'web',
    });
    this.templateCacheService = new ReviewTemplateCacheService({
      cacheService,
      database: offlineDatabase,
      onlineApi: reviewCoreApi,
      ensureOnlineState: () => this.ensureOnlineState(),
      getIsOnline: () => this.isOnline,
      isWeb: () => Platform.OS === 'web',
    });
    this.readService = new ReviewReadService({
      cacheService,
      offlineReviewService: this.offlineReviewService,
      templateCacheService: this.templateCacheService,
      onlineApi: reviewCoreApi,
      refreshOnlineState: () => this.refreshOnlineState(),
      getIsOnline: () => this.isOnline,
      useOfflineWorkflow: shouldUseOfflineReviewWorkflow,
      isWeb: () => Platform.OS === 'web',
    });
    this.lifecycleService = new ReviewLifecycleService({
      onlineApi: reviewCoreApi,
    });
    this.snapshotCacheService = new ReviewSnapshotCacheService({
      database: offlineDatabase,
      offlineReviewService: this.offlineReviewService,
      onlineApi: reviewCoreApi,
      getTemplates: () => this.templateCacheService.getTemplates(),
      ensureOnlineState: () => this.ensureOnlineState(),
      getIsOnline: () => this.isOnline,
      isWeb: () => Platform.OS === 'web',
    });
    this.roomService = new ReviewRoomService({
      cacheService,
      database: offlineDatabase,
      offlineReviewService: this.offlineReviewService,
      onlineApi: reviewCoreApi,
      refreshOnlineState: () => this.refreshOnlineState(),
      getIsOnline: () => this.isOnline,
      useOfflineWorkflow: shouldUseOfflineReviewWorkflow,
      isWeb: () => Platform.OS === 'web',
    });
    this.onDemandItemService = new ReviewOnDemandItemService({
      offlineReviewService: this.offlineReviewService,
      onlineApi: reviewCoreApi,
      getIsOnline: () => this.isOnline,
      useOfflineWorkflow: shouldUseOfflineReviewWorkflow,
      isWeb: () => Platform.OS === 'web',
    });
    this.metadataService = new ReviewMetadataService({
      database: offlineDatabase,
      onlineApi: reviewCoreApi,
      offlineReviewService: this.offlineReviewService,
      useOfflineWorkflow: shouldUseOfflineReviewWorkflow,
    });
    this.responseService = new ReviewResponseService({
      offlineReviewService: this.offlineReviewService,
      onlineApi: reviewCoreApi,
      getIsOnline: () => this.isOnline,
      useOfflineWorkflow: shouldUseOfflineReviewWorkflow,
      isWeb: () => Platform.OS === 'web',
    });
    this.photoService = new ReviewPhotoService({
      photoApi: reviewPhotoApi,
      getReviewItemDetail: (reviewId, itemId) =>
        this.lifecycleService.getReviewItemDetail(reviewId, itemId),
      refreshOnlineState: () => this.refreshOnlineState(),
      getIsOnline: () => this.isOnline,
      isWeb: () => Platform.OS === 'web',
    });
  }

  /**
   * Initialize network state listener
   */
  private async initializeNetworkListener(): Promise<void> {
    // Get initial network state
    const initialState = await NetInfo.fetch();
    this.isOnline = applyDetoxNetworkOverride(initialState);

    // Listen for changes
    NetInfo.addEventListener((state) => {
      this.isOnline = applyDetoxNetworkOverride(state);
    });

    subscribeToDetoxNetworkOverride((isOnline) => {
      this.isOnline = isOnline;
    });
  }

  /**
   * Check if service is currently online
   */
  isServiceOnline(): boolean {
    return this.isOnline;
  }
  async getUserReviews(): Promise<HomeReview[]> {
    return this.readService.getUserReviews();
  }

  async getTemplates(): Promise<ReviewTemplate[]> {
    return this.templateCacheService.getTemplates();
  }

  async getTemplateDetails(templateId: number, options?: { preferFresh?: boolean }): Promise<ReviewTemplate> {
    return this.templateCacheService.getTemplateDetails(templateId, options);
  }

  async createReview(
    propertyId: number,
    templateId: number,
    address?: string,
    detailsOrOptions?: ReviewContactDetails | ReviewCreateOptions,
    options?: ReviewCreateOptions
  ): Promise<HomeReview> {
    return this.creationService.createReview(
      propertyId,
      templateId,
      address,
      detailsOrOptions,
      options
    );
  }

  async getReviewDetails(
    reviewId: number,
    options?: ReviewDetailsOptions
  ): Promise<HomeReview> {
    return this.readService.getReviewDetails(reviewId, options);
  }

  async getReviewById(
    reviewId: number,
    options?: { includePhotos?: boolean; presignPhotos?: boolean }
  ): Promise<HomeReview> {
    return this.getReviewDetails(reviewId, options);
  }

  async refreshTemplateCache(): Promise<void> {
    return this.templateCacheService.refreshTemplateCache();
  }

  async preloadReviewSnapshots(reviews: HomeReview[]): Promise<void> {
    return this.snapshotCacheService.preloadReviewSnapshots(reviews);
  }

  private async ensureOnlineState(): Promise<boolean> {
    if (this.isOnline) {
      return true;
    }

    try {
      const netState = await NetInfo.fetch();
      this.isOnline = applyDetoxNetworkOverride(netState);
    } catch {
      // ignore
    }

    return this.isOnline;
  }

  private async refreshOnlineState(): Promise<boolean> {
    const netState = await NetInfo.fetch().catch(() => ({ isConnected: false }));
    this.isOnline = applyDetoxNetworkOverride(netState);
    return this.isOnline;
  }

  async getReviewItemDetail(
    reviewId: number,
    itemId: number
  ): Promise<{ review: Partial<HomeReview>; item: ReviewItem; response: ReviewResponse | null }> {
    return this.lifecycleService.getReviewItemDetail(reviewId, itemId);
  }

  async getPhotoPresignedUrls(photoIds: number[], expiresIn?: number): Promise<PresignedPhotoUrl[]> {
    return this.photoService.getPhotoPresignedUrls(photoIds, expiresIn);
  }

  async updateResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewResponse> {
    return this.responseService.updateResponse(reviewId, itemId, data, options);
  }

  async updateSubmittedResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>
  ): Promise<ReviewResponse> {
    return this.responseService.updateSubmittedResponse(reviewId, itemId, data);
  }

  async submitReview(reviewId: number, vendorNotes?: string): Promise<ReviewLifecycleResponse> {
    return this.lifecycleService.submitReview(reviewId, vendorNotes);
  }

  async completeReview(reviewId: number): Promise<ReviewLifecycleResponse> {
    return this.lifecycleService.completeReview(reviewId);
  }

  async searchReviewItems(query: string, reviewId?: number): Promise<ReviewItem[]> {
    return this.lifecycleService.searchReviewItems(query, reviewId);
  }

  async uploadPhotos(
    reviewId: number,
    responseId: number,
    itemId: number,
    photos: PhotoUploadInput[],
    questionId?: string
  ): Promise<UploadedPhoto[]> {
    return this.photoService.uploadPhotos(reviewId, responseId, itemId, photos, questionId);
  }

  async deletePhoto(photoId: number): Promise<void> {
    return this.photoService.deletePhoto(photoId);
  }

  async updatePhotoNote(photoId: number, note: string): Promise<Photo> {
    if (Platform.OS === 'web') {
      return this.photoService.updatePhotoNote(photoId, note);
    }

    await offlineDatabase.updatePhotoMetadataNote(photoId, note, 'pending');
    const isOnline = await this.refreshOnlineState();
    if (!isOnline) {
      const localPhoto = await offlineDatabase.getPhotoMetadataById(photoId);
      return { ...(localPhoto || {}), id: photoId, note } as Photo;
    }

    try {
      const updatedPhoto = await this.photoService.updatePhotoNote(photoId, note);
      await offlineDatabase.updatePhotoMetadataNote(
        photoId,
        updatedPhoto.note ?? note,
        'synced'
      );
      return updatedPhoto;
    } catch (error) {
      await offlineDatabase.updatePhotoMetadataNote(
        photoId,
        note,
        'failed',
        error instanceof Error ? error.message : 'Photo note sync failed'
      );
      if (!await this.refreshOnlineState()) {
        const localPhoto = await offlineDatabase.getPhotoMetadataById(photoId);
        return { ...(localPhoto || {}), id: photoId, note } as Photo;
      }
      throw error;
    }
  }

  invalidateResponsePhotoCache(responseId: number): void {
    this.photoService.invalidateResponsePhotoCache(responseId);
  }

  async getPhotosForQuestion(
    reviewId: number,
    responseId: number,
    itemId?: number,
    questionId?: string
  ): Promise<any[]> {
    return this.photoService.getPhotosForQuestion(reviewId, responseId, itemId, questionId);
  }

  async getRooms(propertyId?: number): Promise<Room[]> {
    return this.roomService.getRooms(propertyId);
  }

  async createRoom(propertyId: number, name: string, options?: { forceOnline?: boolean }): Promise<Room> {
    return this.roomService.createRoom(propertyId, name, options);
  }

  async updateRoom(roomId: number, data: { name?: string; displayOrder?: number }): Promise<Room> {
    return this.roomService.updateRoom(roomId, data);
  }

  async deleteRoom(roomId: number): Promise<void> {
    return this.roomService.deleteRoom(roomId);
  }

  async deleteReview(reviewId: number): Promise<void> {
    return this.readService.deleteReview(reviewId);
  }

  // Vendor email methods
  async generateVendorEmails(reviewId: number, feedback?: Record<number, string>): Promise<any> {
    return reviewVendorEmailApi.generate(reviewId, feedback);
  }

  async getVendorEmails(reviewId: number): Promise<any> {
    return reviewVendorEmailApi.getAll(reviewId);
  }

  async getVendorEmailJobStatus(reviewId: number, jobId?: string): Promise<any> {
    return reviewVendorEmailApi.getJobStatus(reviewId, jobId);
  }

  async updateVendorEmailStatus(
    reviewId: number,
    vendorTypeId: number,
    status: VendorEmailStatus
  ): Promise<any> {
    return reviewVendorEmailApi.updateStatus(reviewId, vendorTypeId, status);
  }

  async updateVendorEmailContent(
    reviewId: number,
    vendorTypeId: number,
    subject: string,
    content: string
  ): Promise<any> {
    return reviewVendorEmailApi.updateContent(reviewId, vendorTypeId, subject, content);
  }

  async sendVendorEmail(
    reviewId: number,
    vendorTypeId: number,
    vendorIds?: number[]
  ): Promise<SendVendorEmailResult> {
    return reviewVendorEmailApi.send(reviewId, vendorTypeId, vendorIds);
  }

  // Alias methods for compatibility with HomeScreen.tsx
  async updateReviewResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewResponse> {
    return this.updateResponse(reviewId, itemId, data, options);
  }

  async updateSubmittedReviewResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>
  ): Promise<ReviewResponse> {
    return this.updateSubmittedResponse(reviewId, itemId, data);
  }

  // On-demand items methods
  async getOnDemandItems(reviewId: number): Promise<ReviewItem[]> {
    return this.onDemandItemService.getOnDemandItems(reviewId);
  }

  async createOnDemandItem(
    reviewId: number,
    data: OnDemandItemInput,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewItem> {
    return this.onDemandItemService.createOnDemandItem(reviewId, data, options);
  }

  async updateOnDemandItem(
    reviewId: number,
    itemId: number,
    data: OnDemandItemUpdate,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewItem> {
    return this.onDemandItemService.updateOnDemandItem(reviewId, itemId, data, options);
  }

  async deleteOnDemandItem(reviewId: number, itemId: number): Promise<void> {
    return this.onDemandItemService.deleteOnDemandItem(reviewId, itemId);
  }

  async updateReviewNotes(reviewId: number, notes: string): Promise<HomeReview> {
    return this.metadataService.updateReviewNotes(reviewId, notes);
  }

  async updateReviewOnDemandProjects(reviewId: number, onDemandProjects: string): Promise<HomeReview> {
    return this.metadataService.updateReviewOnDemandProjects(reviewId, onDemandProjects);
  }

  async updateReviewDetails(
    reviewId: number,
    details: ReviewContactDetails
  ): Promise<HomeReview> {
    return this.metadataService.updateReviewDetails(reviewId, details);
  }

  async getReviewReportDeliveries(reviewId: number): Promise<ReviewReportDelivery[]> {
    return reviewCoreApi.getReviewReportDeliveries(reviewId);
  }

  async resendReviewReport(
    reviewId: number
  ): Promise<ReviewReportResendResult> {
    return reviewCoreApi.resendReviewReport(reviewId);
  }

  async resendReviewReportWithDetails(
    reviewId: number,
    details: ReviewContactDetails
  ): Promise<
    | { queued: false; reason: 'offline' }
    | { queued: true; result: ReviewReportResendResult }
  > {
    if (!await getEffectiveNetworkOnline()) {
      return { queued: false, reason: 'offline' };
    }

    // Native review edits are local-first. Force this particular write to the
    // server and await it before requesting a new immutable report revision so
    // the delivery cannot be generated with stale recipient details.
    await this.metadataService.updateReviewDetails(reviewId, details, { forceOnline: true });
    const result = await reviewCoreApi.resendReviewReport(reviewId);
    return { queued: true, result };
  }

  // ============ Admin Item Management ============

  /**
   * Create a new review item
   */
  async createItem(itemData: AdminReviewItemInput): Promise<AdminReviewItem> {
    return reviewAdminApi.createItem(itemData);
  }

  /**
   * Update an existing review item
   */
  async updateItem(itemId: number, updates: AdminReviewItemUpdate): Promise<AdminReviewItem> {
    return reviewAdminApi.updateItem(itemId, updates);
  }

  // ============ Sharing API Calls (Admin Only) ============

  /**
   * Link categories across templates
   */
  async linkCategories(sourceCategoryId: number, targetCategoryId: number): Promise<SharedCategoryLink> {
    return reviewAdminApi.linkCategories(sourceCategoryId, targetCategoryId);
  }

  /**
   * Remove a category link
   */
  async unlinkCategories(linkId: number): Promise<void> {
    return reviewAdminApi.unlinkCategories(linkId);
  }

  /**
   * Get all links for a category
   */
  async getCategoryLinks(categoryId: number): Promise<SharedCategoryLink[]> {
    return reviewAdminApi.getCategoryLinks(categoryId);
  }

  /**
   * Link items across templates with question mappings
   */
  async linkItems(
    sourceItemId: number,
    targetItemId: number,
    questionMappings: QuestionMappingInput[],
    options?: ItemLinkOptions
  ): Promise<SharedItemLink> {
    return reviewAdminApi.linkItems(sourceItemId, targetItemId, questionMappings, options);
  }

  /**
   * Remove an item link
   */
  async unlinkItems(linkId: number): Promise<void> {
    return reviewAdminApi.unlinkItems(linkId);
  }

  /**
   * Get all links for an item
   */
  async getItemLinks(itemId: number): Promise<SharedItemLink[]> {
    return reviewAdminApi.getItemLinks(itemId);
  }

  /**
   * Add a question mapping to an item link
   */
  async addQuestionMapping(
    itemLinkId: number,
    sourceQuestionId: string,
    targetQuestionId: string,
    sharedKey: string
  ): Promise<SharedQuestionMapping> {
    return reviewAdminApi.addQuestionMapping(itemLinkId, sourceQuestionId, targetQuestionId, sharedKey);
  }

  /**
   * Remove a question mapping
   */
  async removeQuestionMapping(mappingId: number): Promise<void> {
    return reviewAdminApi.removeQuestionMapping(mappingId);
  }

  /**
   * Get question mappings for an item link
   */
  async getQuestionMappings(linkId: number): Promise<SharedQuestionMapping[]> {
    return reviewAdminApi.getQuestionMappings(linkId);
  }

  /**
   * Get all item links for a template (bulk query)
   */
  async getTemplateLinks(templateId: number): Promise<SharedItemLink[]> {
    return reviewAdminApi.getTemplateLinks(templateId);
  }
}

export const reviewService = new ReviewService();
